import {
  ClockCircleOutlined,
  CloudSyncOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import {
  useCreateWorker,
  useDeleteWorker,
  useUpdateAllWorkers,
  useUpdateWorker,
  useWorkerHistory,
  useWorkers,
  useWorkersLive,
} from '../api/hooks';
import type { Worker, WorkerHistoryItem, WorkerLiveState } from '../api/types';
import { extractErrorMessage } from '../api/client';
import { WorkerStatusTag } from '../utils/status';
import { formatDateTime } from '../utils/format';

const { Title, Text } = Typography;

function downloadInstaller(id: string, platform: 'windows' | 'linux') {
  const a = document.createElement('a');
  a.href = `/admin/api/workers/${id}/installer?platform=${platform}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function WorkersPage() {
  const { message } = AntApp.useApp();
  const list = useWorkers();
  const update = useUpdateWorker();
  const del = useDeleteWorker();
  const updateAll = useUpdateAllWorkers();

  const onUpdateAll = async () => {
    try {
      const r = await updateAll.mutateAsync();
      if (r.sent > 0) {
        message.success(
          `Đã gửi yêu cầu cập nhật tới ${r.sent} worker. Worker sẽ tự tải bản mới và khởi động lại (không mất job).`,
        );
      } else {
        message.warning('Không có worker nào đang kết nối để cập nhật.');
      }
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [editWorker, setEditWorker] = useState<Worker | undefined>();
  const [historyWorker, setHistoryWorker] = useState<Worker | undefined>();
  const [realtimeOpen, setRealtimeOpen] = useState(false);

  const onToggle = async (id: string, enabled: boolean) => {
    try {
      await update.mutateAsync({ id, body: { enabled } });
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await del.mutateAsync(id);
      message.success('Đã xóa worker');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const pct = (v?: number | null) => Math.max(0, Math.min(100, Math.round(v ?? 0)));

  const renderConfig = (r: Worker) => {
    if (r.cpuCores != null && r.totalMemoryMb != null) {
      return `${r.cpuCores} CPU • ${Math.round(r.totalMemoryMb / 1024)} GB`;
    }
    return '—';
  };

  const columns = [
    {
      title: 'Tên / Host',
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, r: Worker) => (
        <div>
          <Space size={6}>
            <Text>{r.name}</Text>
            {r.role === 'server' && <Tag color="gold">Server</Tag>}
          </Space>
          {r.note && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.note}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    { title: 'Nền tảng', dataIndex: 'platform', key: 'platform', render: (v: string) => v || '—' },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: Worker['status']) => <WorkerStatusTag status={s} />,
    },
    {
      title: 'Cấu hình',
      key: 'config',
      render: (_: unknown, r: Worker) => renderConfig(r),
    },
    {
      title: 'CPU',
      dataIndex: 'cpu',
      key: 'cpu',
      width: 120,
      render: (v: number | null) => <Progress percent={pct(v)} size="small" />,
    },
    {
      title: 'RAM',
      dataIndex: 'memory',
      key: 'memory',
      width: 120,
      render: (v: number | null) => <Progress percent={pct(v)} size="small" />,
    },
    {
      title: 'Đồng thời',
      key: 'jobs',
      width: 110,
      render: (_: unknown, r: Worker) => `${r.activeJobs ?? 0} / ${r.concurrency ?? 0}`,
    },
    {
      title: 'Phiên bản',
      dataIndex: 'version',
      key: 'version',
      render: (v: string) => v || '—',
    },
    {
      title: 'Lần cuối thấy',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      width: 100,
      render: (v: string) => {
        const [d, t] = formatDateTime(v).split(' ');
        return (
          <div style={{ lineHeight: 1.3, whiteSpace: 'nowrap' }}>
            <div>{d}</div>
            {t && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Bật',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled: boolean, r: Worker) => (
        <Switch checked={enabled} onChange={(v) => onToggle(r.id, v)} />
      ),
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 240,
      render: (_: unknown, r: Worker) => (
        <Space>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'windows',
                  label: 'Windows (.zip)',
                  onClick: () => {
                    message.loading('Đang chuẩn bị gói cài đặt...', 2);
                    downloadInstaller(r.id, 'windows');
                  },
                },
                {
                  key: 'linux',
                  label: 'Ubuntu / Linux (.zip)',
                  onClick: () => {
                    message.loading('Đang chuẩn bị gói cài đặt...', 2);
                    downloadInstaller(r.id, 'linux');
                  },
                },
              ],
            }}
          >
            <Button size="small" icon={<DownloadOutlined />}>
              Tải file cài
            </Button>
          </Dropdown>
          <Button
            size="small"
            icon={<EditOutlined />}
            title="Sửa"
            onClick={() => setEditWorker(r)}
          />
          <Button
            size="small"
            icon={<ClockCircleOutlined />}
            title="Lịch sử"
            onClick={() => setHistoryWorker(r)}
          />
          <Popconfirm
            title="Xóa worker này?"
            description="Worker đang chạy sẽ tự kết nối lại; để gỡ hẳn hãy gỡ cài đặt trên máy đó."
            okText="Xóa"
            cancelText="Hủy"
            onConfirm={() => onDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} title="Xóa" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Worker
        </Title>
        <Space>
          <Text type="secondary">Tự động làm mới mỗi 5 giây</Text>
          <Button icon={<ThunderboltOutlined />} onClick={() => setRealtimeOpen(true)}>
            Trạng thái realtime
          </Button>
          <Popconfirm
            title="Cập nhật tất cả worker?"
            description="Tất cả worker đang kết nối sẽ tự tải bản mới nhất và khởi động lại. Job đang chạy không bị mất."
            okText="Cập nhật"
            cancelText="Hủy"
            onConfirm={onUpdateAll}
          >
            <Button icon={<CloudSyncOutlined />} loading={updateAll.isPending}>
              Cập nhật tất cả
            </Button>
          </Popconfirm>
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()}>
            Làm mới
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Thêm Worker
          </Button>
        </Space>
      </Space>

      <Table<Worker>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />

      <CreateWorkerModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditWorkerModal worker={editWorker} onClose={() => setEditWorker(undefined)} />
      <HistoryDrawer worker={historyWorker} onClose={() => setHistoryWorker(undefined)} />
      <RealtimeModal open={realtimeOpen} onClose={() => setRealtimeOpen(false)} />
    </div>
  );
}

// ---------- Modal trạng thái realtime ----------
function RealtimeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const live = useWorkersLive(open, 1500);
  const pct = (v?: number | null) => Math.max(0, Math.min(100, Math.round(v ?? 0)));

  const columns = [
    { title: 'Worker', dataIndex: 'name', key: 'name' },
    {
      title: 'Vai trò',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (role: string) =>
        role === 'server' ? <Tag color="gold">Server (điều phối)</Tag> : <Tag color="blue">Worker (chạy job)</Tag>,
    },
    {
      title: 'Kết nối',
      key: 'conn',
      width: 110,
      render: (_: unknown, r: WorkerLiveState) =>
        r.lastSeenMsAgo < 30000 ? <Tag color="success">Trực tuyến</Tag> : <Tag color="warning">Chậm nhịp</Tag>,
    },
    {
      title: 'CPU',
      dataIndex: 'cpu',
      key: 'cpu',
      width: 140,
      render: (v: number) => <Progress percent={pct(v)} size="small" />,
    },
    {
      title: 'RAM',
      dataIndex: 'memory',
      key: 'memory',
      width: 140,
      render: (v: number) => <Progress percent={pct(v)} size="small" />,
    },
    {
      title: 'Đang chạy',
      key: 'active',
      render: (_: unknown, r: WorkerLiveState) => (
        <Tag color={r.activeJobs > 0 ? 'processing' : 'default'}>
          {r.activeJobs} / {r.concurrency} job
        </Tag>
      ),
    },
    {
      title: 'Slot trống',
      dataIndex: 'freeSlots',
      key: 'freeSlots',
      render: (v: number) => v,
    },
    {
      title: 'Nhận job',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (v: boolean) => (v ? <Tag color="success">Bật</Tag> : <Tag>Tắt</Tag>),
    },
  ];

  return (
    <Modal
      title="Trạng thái chạy realtime (cập nhật mỗi 1.5s)"
      open={open}
      onCancel={onClose}
      footer={null}
      width="90vw"
      style={{ maxWidth: 1180 }}
      destroyOnClose
    >
      <Table<WorkerLiveState>
        rowKey="workerId"
        size="small"
        loading={live.isLoading}
        dataSource={live.data ?? []}
        columns={columns}
        pagination={false}
        locale={{ emptyText: <Empty description="Chưa có worker nào đang kết nối" /> }}
      />
    </Modal>
  );
}

// ---------- Modal sửa worker ----------
function EditWorkerModal({
  worker,
  onClose,
}: {
  worker?: Worker;
  onClose: () => void;
}) {
  const { message } = AntApp.useApp();
  const update = useUpdateWorker();
  const [form] = Form.useForm();

  useEffect(() => {
    if (worker) {
      form.setFieldsValue({
        enabled: worker.enabled,
        note: worker.note ?? '',
        concurrency: worker.concurrency ?? 1,
      });
    }
  }, [worker, form]);

  const onSave = async () => {
    if (!worker) return;
    const v = await form.validateFields();
    try {
      await update.mutateAsync({
        id: worker.id,
        body: { enabled: v.enabled, note: v.note ?? '', concurrency: v.concurrency },
      });
      message.success('Đã lưu thay đổi');
      onClose();
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  return (
    <Modal
      title="Sửa Worker"
      open={!!worker}
      onCancel={onClose}
      onOk={onSave}
      confirmLoading={update.isPending}
      okText="Lưu"
      cancelText="Hủy"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="enabled" label="Bật nhận job" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="concurrency"
          label="Số job chạy song song (đồng thời)"
          tooltip="Số job tối đa worker này nhận cùng lúc. Áp dụng ngay cho worker đang kết nối."
          rules={[{ required: true, message: 'Vui lòng nhập số job song song' }]}
        >
          <InputNumber min={1} max={500} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea rows={3} placeholder="Ghi chú về worker này..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------- Drawer lịch sử chạy ----------
function HistoryDrawer({
  worker,
  onClose,
}: {
  worker?: Worker;
  onClose: () => void;
}) {
  const { data, isLoading } = useWorkerHistory(worker?.id, !!worker);

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: 'Năng lực / Model',
      key: 'capability',
      render: (_: unknown, r: WorkerHistoryItem) =>
        r.job?.model?.name ?? r.job?.modelGroup?.name ?? r.job?.capability?.name ?? '—',
    },
    {
      title: 'Nhà cung cấp',
      key: 'provider',
      render: (_: unknown, r: WorkerHistoryItem) => r.provider?.name ?? '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: WorkerHistoryItem['status']) =>
        s === 'success' ? (
          <Tag color="success">Thành công</Tag>
        ) : (
          <Tag color="error">Thất bại</Tag>
        ),
    },
    {
      title: 'Lỗi',
      key: 'error',
      render: (_: unknown, r: WorkerHistoryItem) => r.errorMessage || '—',
    },
  ];

  return (
    <Drawer
      title={`Lịch sử chạy — ${worker?.name ?? ''}`}
      width={760}
      open={!!worker}
      onClose={onClose}
      destroyOnClose
    >
      <Table<WorkerHistoryItem>
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={data ?? []}
        columns={columns}
        pagination={false}
        locale={{
          emptyText: <Empty description="Chưa có lịch sử chạy nào" />,
        }}
      />
    </Drawer>
  );
}

// ---------- Modal thêm worker mới ----------
function CreateWorkerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { message } = AntApp.useApp();
  const create = useCreateWorker();
  const [form] = Form.useForm();

  const onSave = async () => {
    const v = await form.validateFields();
    try {
      await create.mutateAsync({
        name: v.name.trim(),
        concurrency: v.concurrency,
        note: v.note ?? '',
      });
      message.success('Đã thêm worker');
      form.resetFields();
      onClose();
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  return (
    <Modal
      title="Thêm Worker"
      open={open}
      onCancel={onClose}
      onOk={onSave}
      confirmLoading={create.isPending}
      okText="Lưu"
      cancelText="Hủy"
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ concurrency: 2 }}>
        <Form.Item
          name="name"
          label="Tên Worker"
          rules={[{ required: true, message: 'Vui lòng nhập tên worker' }]}
        >
          <Input placeholder="Ví dụ: May-render-01" />
        </Form.Item>
        <Form.Item name="concurrency" label="Số job chạy song song">
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea rows={3} placeholder="Ghi chú về worker này..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}
